import { datadogRum } from '@datadog/browser-rum';
import NextRouter from 'next/router';
import type { ReactNode } from 'react';
import { useEffectOnce } from 'react-use';
import { InteractionType } from '@/common/types/interaction';
import {
  getInteractionInformation,
  getRouteFromPathname,
  trackClientGroup,
  trackPropertyInteraction,
  trackReferral,
} from './helpers';

export const UserTrackingProvider = ({ children }: { children: ReactNode }) => {
  useEffectOnce(() => {
    const trackLastViewed = async () => {
      if (!NextRouter.pathname) return;
      const { pathname } = NextRouter;

      try {
        const path = getRouteFromPathname(window.location.pathname);
        const { interactionId, interactionType } = getInteractionInformation();
        if (!interactionType || !interactionId) return;

        if (interactionType === InteractionType.clientGroup) {
          await trackClientGroup({ path, interactionId });
        } else if (interactionType === InteractionType.referral) {
          await trackReferral({ path, interactionId });
        } else {
          await trackPropertyInteraction({
            path,
            interactionId,
            pathname,
            interactionType,
          });
        }
      } catch (error) {
        datadogRum.addError(error, {
          message: 'Failed to update last viewed location',
        });
      }
    };

    // Shallow navigations (e.g. syncing a tab to a query param) keep the user on
    // the same interaction, so re-tracking the last viewed location is redundant
    // — and would fire an extra interaction fetch on every such URL change.
    const doTrackLastViewed = (
      _url?: string,
      options?: { shallow?: boolean },
    ) => {
      if (options?.shallow) return;
      void trackLastViewed();
    };

    NextRouter.events.on('routeChangeComplete', doTrackLastViewed);

    return () => {
      NextRouter.events.off('routeChangeComplete', doTrackLastViewed);
    };
  });

  return children;
};
